import TPEAction from 'src/models/common/TPEAction';
import { parseBulkIdentifiers } from 'src/components/calculations/bulk-promote/bulkIdentifierClientParser';
import { PromotionComponentType } from 'src/models/promotion/PromotionTypes';
import { BulkPromotePhase } from 'src/models/promotion/bulkPromotionEnums';
import { BULK_PROMOTE_BATCH_CAP } from 'src/models/promotion/bulkPromotionConstants';
import { mergePreflightFrame } from 'src/components/calculations/bulk-promote/bulkPromotionRecord';
import {
    IdentifierResolution,
    ResolvedIdentifier,
} from 'src/components/calculations/bulk-promote/bulkIdentifierClientParser';
import {
    NameResolution,
    isNameResolvable,
    resolveNames,
} from 'src/components/calculations/bulk-promote/bulkNameResolver';
import {
    BulkPromoteStep,
    BulkPromotionState,
    PHASE_RANK,
    initialState,
} from './BulkPromotionState';

/**
 * List here the actions supported by this reducer
 */
export const ACTIONS = Object.freeze({
    RESET_STATE: new TPEAction('RESET_STATE'),
    SET_LOADING: new TPEAction('SET_LOADING'),
    SET_COMPONENT_TYPE: new TPEAction('SET_COMPONENT_TYPE'),
    /** The (id, name) list for the selected type has arrived, so pasted names can now resolve. */
    SET_NAMED_COMPONENTS: new TPEAction('SET_NAMED_COMPONENTS'),
    SET_PASTED_IDENTIFIERS: new TPEAction('SET_PASTED_IDENTIFIERS'),
    PREFLIGHT_SUBMITTED: new TPEAction('PREFLIGHT_SUBMITTED'),
    PREFLIGHT_ACCEPTED: new TPEAction('PREFLIGHT_ACCEPTED'),
    SET_PHASE: new TPEAction('SET_PHASE'),
    SET_VERDICT: new TPEAction('SET_VERDICT'),
    /** One frame of the asynchronous preflight report. Several make up one verdict. */
    VERDICT_FRAME: new TPEAction('VERDICT_FRAME'),
    /** The verdict has not arrived within the patience window. Nothing is cancelled. */
    VERDICT_OVERDUE: new TPEAction('VERDICT_OVERDUE'),
    /** The connection dropped with a verdict or an outcome outstanding. */
    PUSH_LOST: new TPEAction('PUSH_LOST'),
    EXECUTE_SUBMITTED: new TPEAction('EXECUTE_SUBMITTED'),
    EXECUTION_COMPLETE: new TPEAction('EXECUTION_COMPLETE'),
    SET_FAILED: new TPEAction('SET_FAILED'),
});

function isAcceptablePhase(current: string | null, next: string): boolean {
    const nextRank = PHASE_RANK[next];
    if (nextRank === undefined) {
        return false;
    }
    if (current === null) {
        return true;
    }
    return nextRank >= PHASE_RANK[current];
}

/**
 * Re-resolves the paste whenever the paste or the type changes, so the accepted set the submit uses and
 * the per-row summary the operator reads can never disagree.
 *
 * The cap is a constant rather than `state.config?.batchCap`. While it was fetched it was undefined until
 * the configuration arrived, and parseBulkIdentifiers skips the cap check on an undefined cap — so an
 * over-cap paste made in that window reported nothing over and was refused by the server instead.
 */
function withResolvedIdentifiers(state: BulkPromotionState): BulkPromotionState {
    const parsed = parseBulkIdentifiers(
        state.pastedIdentifiers,
        state.componentType as PromotionComponentType,
        BULK_PROMOTE_BATCH_CAP,
    );
    /*
     * Name resolution runs over the parser's OUTPUT rather than inside it, so an id-only paste follows
     * exactly the path it always did and this cannot regress it. MDA and TPA components are addressed by an
     * opaque id while the name an operator reads off a screen is something else entirely, so without this
     * every pasted name came back NOT_FOUND from the service.
     */
    if (!isNameResolvable(state.componentType) || state.namedComponents.length === 0) {
        return { ...state, resolvedRows: parsed.rows, acceptedIds: parsed.acceptedIds, overCapBy: parsed.overCapBy };
    }

    /*
     * A row that FAILED to decode is a name candidate too, and this is the half that was missing.
     *
     * TPA_TEMPLATE and TPA_WORKSHEET are base64-encoded types, so the parser tries to decode every pasted
     * value first. A name is not valid base64, so it came back DECODE_FAILED — or worse, decoded into
     * mojibake and came back DECODED_NOT_AN_ID, which is how "FC Meadowlark" rendered as "#iÚ0ªä". Resolution
     * only looked at rows the parser had ACCEPTED, so for exactly the two types that most need names, no name
     * was ever tried. MDA is not encoded, which is why it worked and TPA did not.
     *
     * The candidate is the decoded id where there is one, and the raw pasted text where the decode failed.
     */
    const nameCandidate = (row: ResolvedIdentifier): string | null => {
        if (row.resolution === IdentifierResolution.ACCEPTED) {
            return row.resolvedId ?? null;
        }
        if (row.resolution === IdentifierResolution.DECODE_FAILED
            || row.resolution === IdentifierResolution.DECODED_NOT_AN_ID) {
            return row.input.trim();
        }
        return null;
    };

    const candidates = parsed.rows.filter((row) => nameCandidate(row) !== null);
    const { rows: nameRows } = resolveNames(
        candidates.map((row) => nameCandidate(row) as string),
        state.namedComponents,
    );

    let next = 0;
    const resolvedRows = parsed.rows.map((row) => {
        if (nameCandidate(row) === null) {
            return row;
        }
        const nameRow = nameRows[next++];
        const failedToDecode = row.resolution !== IdentifierResolution.ACCEPTED;
        if (nameRow.resolution === NameResolution.RESOLVED) {
            // The pasted value was a name. Show the id it resolved to, in the column that already exists
            // for exactly this — what you pasted, and what it became. A row that had failed to decode
            // becomes ACCEPTED, since a resolved name is a better answer than a decode that was never
            // going to succeed.
            return {
                ...row,
                resolution: IdentifierResolution.ACCEPTED,
                decoded: failedToDecode ? false : row.decoded,
                resolvedId: nameRow.componentId,
                message: `Resolved from name "${row.input.trim()}"`,
            };
        }
        /*
         * The decode message is kept for a value that resolved to no name either. For an encoded type
         * "could not decode this ID" is the more actionable guidance than "no such name", because pasting
         * the URL segment is the thing that always works.
         */
        if (failedToDecode) {
            return row;
        }
        if (nameRow.resolution === NameResolution.AMBIGUOUS) {
            return {
                ...row,
                resolution: IdentifierResolution.NAME_AMBIGUOUS,
                resolvedId: undefined,
                message: nameRow.message,
            };
        }
        if (nameRow.resolution === NameResolution.UNKNOWN) {
            return {
                ...row,
                resolution: IdentifierResolution.NAME_NOT_FOUND,
                resolvedId: undefined,
                message: nameRow.message,
            };
        }
        return row;
    });

    // Only rows that are still accepted are submitted, so an ambiguous name promotes nothing.
    const acceptedIds = resolvedRows
        .filter((row) => row.resolution === IdentifierResolution.ACCEPTED)
        .map((row) => row.resolvedId as string);
    const overCapBy = parsed.overCapBy > 0 ? parsed.overCapBy : 0;
    return { ...state, resolvedRows, acceptedIds, overCapBy };
}

export function bulkPromotionReducer(state: BulkPromotionState, action: TPEAction): BulkPromotionState {
    switch (action.type) {
        case ACTIONS.RESET_STATE.type:
            return { ...initialState };
        case ACTIONS.SET_LOADING.type:
            return { ...state, loading: action.payload };
        case ACTIONS.SET_COMPONENT_TYPE.type:
            return withResolvedIdentifiers({ ...state, componentType: action.payload });
        case ACTIONS.SET_NAMED_COMPONENTS.type:
            // Re-resolves immediately: the operator may have pasted names before the list arrived, and a
            // row saying "name not found" must not persist once the answer is available.
            return withResolvedIdentifiers({ ...state, namedComponents: action.payload });
        case ACTIONS.SET_PASTED_IDENTIFIERS.type:
            return withResolvedIdentifiers({ ...state, pastedIdentifiers: action.payload });
        case ACTIONS.PREFLIGHT_SUBMITTED.type:
            return {
                ...state,
                step: BulkPromoteStep.AWAITING_VERDICT,
                phase: null,
                batchId: null,
                // Appended to, so a frame still in flight for ANY batch this tab has replaced cannot be
                // adopted as this one's. The id is null until the submit is acknowledged, so this is the
                // only way to tell an unmatched frame for the new batch from a late one for an old one —
                // and it has to accumulate, since assigning batchId here wrote null over a live guard
                // whenever a submit was made before the previous one was acked.
                supersededBatchIds: state.batchId != null
                    ? [...state.supersededBatchIds, state.batchId]
                    : state.supersededBatchIds,
                acceptedCount: 0,
                preflight: null,
                execution: null,
                barrierWaitMillis: null,
                errorMessage: null,
                // The clock starts at the submit, not at the first frame: the gap being made visible
                // is exactly the one between asking and being answered. Read from the clock rather
                // than from a payload because this action is dispatched without one, and TPEAction
                // instances are shared singletons whose payload field is mutated in place.
                awaitingVerdictSince: Date.now(),
                verdictPagesSeen: [],
                verdictOverdue: false,
                pushLost: false,
                loading: true,
            };
        case ACTIONS.PREFLIGHT_ACCEPTED.type: {
            /*
             * Guarded on the same two things VERDICT_FRAME is. The ack travels over the same socket as the
             * frames, so it is equally subject to late delivery, and adopting a stale one deadlocks the run
             * outright: the old id is taken, its own frames are then refused by the superseded check, the new
             * batch's frames are refused by the id mismatch, and nothing can reach CONFIRMING. The operator
             * is eventually told the verdict is overdue for a batch whose preflight in fact completed.
             */
            if (state.supersededBatchIds.includes(action.payload.batchId)) {
                return state;
            }
            /*
             * AWAITING_VERDICT is the live submit. INPUT is the recovery path, which adopts a batch parked on
             * its verdict from the stored handle and legitimately runs with nothing in flight — an earlier
             * version of this guard named only AWAITING_VERDICT and silently broke it, leaving the recovered
             * batch with no id and Confirm about to send null. No test covered it, which is why it stayed
             * green. The superseded list above is what actually refuses a stale ack, not the step.
             */
            if (state.step !== BulkPromoteStep.AWAITING_VERDICT && state.step !== BulkPromoteStep.INPUT) {
                return state;
            }
            return { ...state, batchId: action.payload.batchId, acceptedCount: action.payload.acceptedCount };
        }
        case ACTIONS.SET_PHASE.type: {
            const { phase, barrierWaitMillis } = action.payload;
            /*
             * Only while a batch is actually in flight. Without this a phase is accepted in INPUT or DONE with
             * nothing running, and because PHASE_RANK is monotonic a late push for a replaced batch could set a
             * high rank that then rejects the next batch's own earlier phases for the whole of its run — the
             * operator watching "waiting at barrier" with a stale wait while the new batch is still resolving.
             *
             * This bounds that rather than closing it. Closing it needs bulkPromotionId on the phase push, the
             * way the frame channel carries it: this payload is only {phase, barrierWaitMillis}, so a late push
             * for a replaced batch cannot be told from a genuine one for the current batch within these steps.
             * That is a service-side change and the asymmetry worth removing next.
             */
            if (state.step !== BulkPromoteStep.AWAITING_VERDICT
                && state.step !== BulkPromoteStep.CONFIRMING
                && state.step !== BulkPromoteStep.EXECUTING) {
                return state;
            }
            if (!isAcceptablePhase(state.phase, phase)) {
                return state;
            }
            // A repeat of the current phase is not new progress, but AT_BARRIER is re-reported with
            // a growing wait, so the wait itself still has to be taken or the display freezes.
            if (state.phase === phase) {
                return barrierWaitMillis === undefined || barrierWaitMillis === state.barrierWaitMillis
                    ? state
                    : { ...state, barrierWaitMillis };
            }
            return {
                ...state,
                phase,
                barrierWaitMillis: barrierWaitMillis ?? state.barrierWaitMillis,
            };
        }
        case ACTIONS.SET_VERDICT.type: {
            /*
             * The STEP is held once the operator has moved on, but the verdict itself is still taken.
             *
             * Unguarded, this dragged the step back from EXECUTING or DONE to CONFIRMING on a duplicate
             * synchronous result or a resume read landing just after confirm, repainting the confirm button
             * over a batch already promoting server-side. Refusing the action outright was the wrong cure: the
             * read-back rebuild legitimately supplies a verdict for a batch that is already terminal, and
             * dropping it left the outcome screen showing the pushed verdict's blocked rows instead of the
             * record's. So the step is pinned and the payload is kept.
             */
            /*
             * A verdict arriving in INPUT with no batch adopted is a late delivery for a flow the operator has
             * already left. SET_FAILED rewinds to INPUT and retires the failed id, and VERDICT_FRAME refuses
             * that batch by id — but this action carries no id, so without this a late verdict re-opened the
             * confirmation for a batch the operator had just been told had failed, with Confirm wired to
             * execute it. Exactly the regression SET_FAILED's own comment warns about, by another route.
             *
             * Keyed on the batch rather than on the step, because the recovery path DOES set a verdict from
             * INPUT: it adopts the parked batch first, so batchId is set by the time this runs. A stray late
             * verdict has no such adoption behind it.
             */
            if (state.step === BulkPromoteStep.INPUT && state.batchId == null) {
                return state;
            }
            const movedOn = state.step === BulkPromoteStep.EXECUTING || state.step === BulkPromoteStep.DONE;
            return {
                ...state,
                step: movedOn ? state.step : BulkPromoteStep.CONFIRMING,
                preflight: action.payload,
                /*
                 * Reset with the verdict it belongs to. This replaces `preflight` wholesale, so leaving the
                 * seen-pages set behind meant a frame arriving afterwards merged against the FULL verdict as
                 * its existing value: every blocked component was then listed twice, with pagesReceived counted
                 * against a page set from the other transport.
                 */
                verdictPagesSeen: [],
                awaitingVerdictSince: null,
                verdictOverdue: false,
                pushLost: false,
                loading: false,
            };
        }
        case ACTIONS.VERDICT_FRAME.type: {
            const frame = action.payload;
            // A frame for some other batch is a stale delivery from an earlier submit in this tab.
            // Taking it would show the operator a verdict for a batch they are not confirming.
            //
            // A frame for the batch a new submit replaced is rejected outright. Before the submit is
            // acknowledged the id is null and cannot be matched, so without this a late frame for the
            // previous batch would be adopted as this one's and every genuine frame then rejected.
            /*
             * Symmetric with SET_VERDICT. A frame arriving in INPUT with no batch adopted is a stray delivery
             * for a flow the operator has left, and neither check below refuses it: RESET_STATE empties the
             * superseded list, and the id-mismatch check is skipped while batchId is null. Without this the
             * frame was adopted and the confirmation reopened for a batch nobody submitted.
             *
             * The recovery path is unaffected: it adopts the parked batch first, so batchId is set while the
             * step is still INPUT.
             */
            if (state.step === BulkPromoteStep.INPUT && state.batchId == null) {
                return state;
            }
            if (state.supersededBatchIds.includes(frame.summary.bulkPromotionId)) {
                return state;
            }
            if (state.batchId != null && frame.summary.bulkPromotionId !== state.batchId) {
                return state;
            }
            // Once the operator has moved on there is nothing a late frame can usefully change, and
            // reopening the confirmation over an executing or finished batch would be a regression.
            if (state.step === BulkPromoteStep.EXECUTING || state.step === BulkPromoteStep.DONE) {
                return state;
            }
            const { verdict, seenPages } = mergePreflightFrame(state.preflight, frame, state.verdictPagesSeen);
            return {
                ...state,
                // Shown on the first frame rather than held back until the last: the summary is
                // complete on every frame, and only the blocked table grows as the rest arrive.
                step: BulkPromoteStep.CONFIRMING,
                batchId: state.batchId ?? frame.summary.bulkPromotionId,
                preflight: verdict,
                verdictPagesSeen: seenPages,
                awaitingVerdictSince: null,
                verdictOverdue: false,
                pushLost: false,
                loading: false,
            };
        }
        case ACTIONS.VERDICT_OVERDUE.type:
            /*
             * CONFIRMING counts as still waiting whenever frames are outstanding. The confirmation opens on
             * frame 1 of N, so a stream that stalls afterwards left this action rejected — the step had already
             * moved on — and the operator watched a verdict that would never complete with no advisory at all.
             */
            return state.step === BulkPromoteStep.AWAITING_VERDICT || state.step === BulkPromoteStep.CONFIRMING
                ? { ...state, verdictOverdue: true, loading: false }
                : state;
        case ACTIONS.PUSH_LOST.type:
            // CONFIRMING for the same reason: frames 2..N are an outcome outstanding, which is exactly what
            // pushLost is documented to mean, and a drop during their delivery could not be reported before.
            return state.step === BulkPromoteStep.AWAITING_VERDICT
                || state.step === BulkPromoteStep.CONFIRMING
                || state.step === BulkPromoteStep.EXECUTING
                ? { ...state, pushLost: true, loading: false }
                : state;
        case ACTIONS.EXECUTE_SUBMITTED.type: {
            /*
             * Only from the confirmation, and only once. With no step guard this accepted a consent from INPUT
             * — a double-click, or after SET_FAILED had rewound the step — and moved to EXECUTING with no
             * batch id at all; from DONE it re-executed a batch that had already finished.
             */
            if (state.step !== BulkPromoteStep.CONFIRMING) {
                return state;
            }
            /*
             * Refused while the blocked list is known to be incomplete. The verdict arrives as N frames and the
             * confirmation opens on the first, so the operator can reach this button having been shown only
             * part of what is blocked — and BulkPromotionTypes says of this very pair that "unequal means
             * frames were lost, so the blocked table below is incomplete and must say so". Consenting on a
             * partial list is consenting to something not yet seen, and this is the one action the flow
             * documents as impossible to take back, so it waits rather than proceeds. PreflightSummary already
             * renders the warning that explains the wait.
             *
             * Only a KNOWN shortfall blocks: both counts have to be present. A verdict that carries no paging
             * information at all is not evidence of a loss, and refusing on absent fields would strand every
             * batch read back through a transport that does not page.
             */
            const pagesReceived = state.preflight?.pagesReceived;
            const totalPages = state.preflight?.totalPages;
            if (pagesReceived !== undefined && totalPages !== undefined && pagesReceived < totalPages) {
                return state;
            }
            return {
                ...state,
                step: BulkPromoteStep.EXECUTING,
                errorMessage: null,
                pushLost: false,
                loading: true,
            };
        }
        case ACTIONS.EXECUTION_COMPLETE.type:
            /*
             * Its payload carries a batchId, so it can be matched — and had to be. A delayed result for an
             * earlier batch otherwise forced DONE with that batch's counts, and the EXECUTING/DONE guards then
             * locked the live batch out of its own completion.
             *
             * A result for a batch this tab replaced is refused outright; otherwise it has to match the batch in
             * flight. An absent batchId on either side is allowed through, because the outcome can be rebuilt
             * from a parked record that never carried one and dropping it would leave the flow stuck on
             * "Processing" for a batch that has finished.
             */
            /*
             * Symmetric with SET_VERDICT and VERDICT_FRAME. In INPUT with no batch adopted neither check below
             * refuses a stray outcome: RESET_STATE empties the superseded list, SET_FAILED retires only its own
             * id, and the mismatch check is skipped while batchId is null. So a late completion push forced the
             * flow to DONE and rendered an outcome for a batch the operator never submitted.
             *
             * Both recovery paths adopt the batch before sending its outcome, so they carry a batchId here.
             */
            if (state.step === BulkPromoteStep.INPUT
                && state.batchId == null
                && action.payload.batchId != null) {
                return state;
            }
            if (action.payload.batchId != null) {
                if (state.supersededBatchIds.includes(action.payload.batchId)) {
                    return state;
                }
                if (state.batchId != null && action.payload.batchId !== state.batchId) {
                    return state;
                }
            }
            return {
                ...state,
                step: BulkPromoteStep.DONE,
                execution: action.payload,
                phase: BulkPromotePhase.COMPLETE,
                // Normalised the way SET_PHASE normalises it. Assigned straight off an `any` payload, an
                // omitted field put undefined into a `number | null` field, and a consumer guarding on
                // `!== null` then rendered NaN.
                barrierWaitMillis: action.payload.barrierWaitMillis ?? state.barrierWaitMillis,
                awaitingVerdictSince: null,
                verdictOverdue: false,
                pushLost: false,
                loading: false,
            };
        case ACTIONS.SET_FAILED.type:
            /*
             * The batch identity goes with the failure. Returning to INPUT while batchId, preflight, phase
             * and execution survived left a later report frame able to pass every guard in VERDICT_FRAME and
             * drive the step back to CONFIRMING — re-opening the confirmation prompt for a batch the operator
             * had just been told had failed, with Confirm wired to execute it. The stale execution also left
             * a finished batch's outcome table rendered underneath the error banner.
             *
             * The failed batch's id is retired into supersededBatchIds rather than dropped with the rest.
             * Nulling batchId alone does not refuse its late frames — it removes the only thing they were
             * matched against, so VERDICT_FRAME's id checks stop applying and the next frame for the batch
             * just declared failed is adopted, which is the regression above by a different route. Listing
             * it is what actually refuses it.
             */
            return {
                ...state,
                step: BulkPromoteStep.INPUT,
                errorMessage: action.payload,
                batchId: null,
                supersededBatchIds: state.batchId != null
                    ? [...state.supersededBatchIds, state.batchId]
                    : state.supersededBatchIds,
                preflight: null,
                execution: null,
                phase: null,
                verdictPagesSeen: [],
                awaitingVerdictSince: null,
                verdictOverdue: false,
                pushLost: false,
                loading: false,
            };
        default:
            return state;
    }
}
